import * as React from "react"

import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "../../cn"
import { primitives } from "../../tokens/primitives"
import { getReadableTextOnFill } from "../../tokens/swatchContrast"

const filterChipVariants = cva(
  [
    "inline-flex items-center gap-1.5",
    "h-control px-2 rounded-md border text-sm",
    "transition-all",
  ],
  {
    variants: {
      selected: {
        true: "border-transparent shadow-sm",
        false:
          "border-coval-border text-coval-text-muted hover:border-coval-border-active hover:text-coval-text",
      },
    },
    defaultVariants: {
      selected: false,
    },
  },
)

export interface FilterChipProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof filterChipVariants> {
  /**
   * Accent color (any CSS color string, including `var(--token)`). Fills the
   * chip when selected and tints the border + leading dot when not. Omit for a
   * neutral chip.
   */
  color?: string
  /** Optional count rendered as "×N" after the label. */
  count?: number
  /**
   * Leading mark in place of the color dot — a type icon, say. It inherits the
   * chip's accent, so an `svg` drawn in `currentColor` needs no color of its
   * own.
   */
  icon?: React.ReactNode
}

/**
 * Resolves a CSS color (a `var(--token)` included) in `node`'s context. It is
 * read off a hidden probe rather than the chip's own background, which
 * `transition-all` animates: a read right after selecting would report the
 * transparent start of the fade.
 */
function resolveColor(node: HTMLElement, color: string): string {
  const probe = node.ownerDocument.createElement("span")
  probe.style.cssText = "position:absolute;visibility:hidden;transition:none"
  probe.style.color = color
  node.appendChild(probe)
  const resolved = getComputedStyle(probe).color
  probe.remove()
  return resolved
}

/** The first opaque background painted behind `node`, for a translucent fill. */
function backdropOf(node: HTMLElement): string {
  for (
    let element = node.parentElement;
    element;
    element = element.parentElement
  ) {
    const background = getComputedStyle(element).backgroundColor
    if (getReadableTextOnFill(background) !== null) {
      return background
    }
  }
  return primitives.white
}

const FilterChip = React.forwardRef<HTMLButtonElement, FilterChipProps>(
  (
    {
      className,
      selected: selectedProp,
      color,
      count,
      icon,
      children,
      type,
      style,
      ...props
    },
    ref,
  ) => {
    const selected = selectedProp ?? false
    const fillColor = selected && color ? color : null
    const buttonRef = React.useRef<HTMLButtonElement | null>(null)
    // The fill can be any CSS color, `var(--token)` included, so the label
    // color is chosen against the fill the browser actually painted. A hex fill
    // is readable before layout, which keeps the first paint right.
    const [measuredOnFill, setMeasuredOnFill] = React.useState<string | null>(
      null,
    )
    const onFill = fillColor
      ? (measuredOnFill ?? getReadableTextOnFill(fillColor) ?? primitives.white)
      : null

    React.useLayoutEffect(() => {
      const node = buttonRef.current
      if (!fillColor || !node) {
        setMeasuredOnFill(null)
        return
      }
      const measure = () => {
        setMeasuredOnFill(
          getReadableTextOnFill(
            resolveColor(node, fillColor),
            backdropOf(node),
          ),
        )
      }
      measure()
      // A theme switch remaps the token behind a `var()` fill without
      // re-rendering the chip.
      const observer = new MutationObserver(measure)
      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class", "style", "data-theme"],
      })
      return () => observer.disconnect()
    }, [fillColor])

    const setRefs = React.useCallback(
      (node: HTMLButtonElement | null) => {
        buttonRef.current = node
        if (typeof ref === "function") {
          ref(node)
        } else if (ref) {
          ref.current = node
        }
      },
      [ref],
    )

    // Unselected chips get a translucent tint of the accent. color-mix keeps
    // that working for any CSS color form — appending a hex alpha pair would
    // only be valid for 6-digit hex and silently voids `var(--token)` accents.
    const colorStyle: React.CSSProperties = color
      ? selected
        ? { backgroundColor: color, color: onFill ?? undefined }
        : { borderColor: `color-mix(in srgb, ${color} 38%, transparent)` }
      : {}

    return (
      <button
        ref={setRefs}
        type={type ?? "button"}
        aria-pressed={selected}
        className={cn(filterChipVariants({ selected, className }))}
        style={{ ...colorStyle, ...style }}
        {...props}
      >
        {icon !== undefined ? (
          <span
            className="flex-none inline-flex items-center justify-center [&>svg]:size-3.5"
            style={color ? { color: onFill ?? color } : undefined}
            aria-hidden="true"
          >
            {icon}
          </span>
        ) : (
          color !== undefined && (
            <span
              className="size-2 rounded-sm flex-none"
              style={{ backgroundColor: onFill ?? color }}
              aria-hidden="true"
            />
          )
        )}
        <span className="font-medium">{children}</span>
        {count !== undefined && <span>×{count}</span>}
      </button>
    )
  },
)

FilterChip.displayName = "FilterChip"

export { FilterChip, filterChipVariants }
